import { Reveal, SectionTag } from "./shared";
import { Waterfall } from "./Waterfall";
import { Database, LineChart, BadgeCheck, FileCheck2, BarChart3, Layers, CalendarRange, Target } from "lucide-react";

const phases = [
  { days: "Days 1–2", title: "Data Access", icon: Database, text: "Stakeholder kickoff with CFO and ops teams. Ingest ERP extracts, financials, production and inventory records. No new systems. No long data-gathering project. Operation runs undisrupted." },
  { days: "Days 3–5", title: "Analysis", icon: LineChart, text: "Load and validate transactional data across the five pillars. Run our analysis framework to isolate working capital leakages and quantify recovery potential by category." },
  { days: "Days 6–7", title: "Validation & Delivery", icon: BadgeCheck, text: "Every number reviewed with your CFO. Executive presentation to leadership and sponsor, with a complete 90-day recovery roadmap for your ops team to execute." },
];

const deliverables = [
  { icon: Target, name: "Total Trapped Cash Estimate", text: "Quantified opportunity across all five pillars" },
  { icon: BarChart3, name: "Cash Waterfall Analysis", text: "Visual breakdown of where cash is leaking and how to recover it" },
  { icon: Layers, name: "Trapped Cash by Pillar", text: "Ranked findings with root causes and operational drivers" },
  { icon: CalendarRange, name: "90-Day Implementation Plan", text: "Month-by-month roadmap — Month 1: 30–40% | Month 2: 50–60% | Month 3: 80%+" },
  { icon: FileCheck2, name: "Working Capital Benchmark", text: "Peer benchmark and achievable target state" },
];

export const SevenDays = () => (
  <section id="seven-days" className="py-12 lg:py-16 bg-white" data-testid="seven-days-section">
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <SectionTag num="04" label="The Diagnostic" />
      <Reveal>
        <h2 className="font-display font-extrabold text-[#0B1B2B] tracking-[-0.02em] leading-[1.05] text-3xl sm:text-4xl lg:text-[44px] max-w-[720px]">
          The seven days <span className="text-[#3B7BC8]">of Discovery.</span>
        </h2>
      </Reveal>

      <div className="mt-8 grid lg:grid-cols-3 gap-5 relative">
        {phases.map((p, i) => (
          <Reveal key={p.title} delay={0.1 * i} className="h-full">
            <div className="relative h-full p-8 rounded-2xl bg-[#f7f9fa] border border-[#0B1B2B]/8 hover:bg-white hover:shadow-[0_20px_50px_rgba(11,27,43,0.09)] transition-all duration-500" data-testid={`phase-card-${i + 1}`}>
              <div className="flex items-center justify-between">
                <span className="px-4 py-2 rounded-full bg-[#0B1B2B] text-white text-[14px] font-bold tracking-[0.08em]">{p.days}</span>
                <p.icon className="w-7 h-7 text-[#0E5F46]" strokeWidth={1.8} />
              </div>
              <h3 className="mt-6 font-display font-bold text-[#0B1B2B] text-2xl">{p.title}</h3>
              <p className="mt-3 text-[#3c4a58] text-[15.5px] leading-relaxed">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-10 rounded-3xl bg-[#0B1B2B] p-8 lg:p-12 relative overflow-hidden" data-testid="deliverables-panel">
          <div className="absolute inset-0 dot-grid-dark pointer-events-none" />
          <div className="relative">
            <p className="text-[13px] uppercase tracking-[0.2em] font-bold text-[#7fd6b8] mb-8">What You Receive</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-8">
              {deliverables.map((d) => (
                <div key={d.name} className="group">
                  <d.icon className="w-6 h-6 text-[#7fd6b8] group-hover:scale-110 transition-transform duration-300" strokeWidth={1.8} />
                  <h4 className="mt-4 font-display font-bold text-white text-base leading-snug">{d.name}</h4>
                  <p className="mt-2 text-white/75 text-sm leading-relaxed">{d.text}</p>
                </div>
              ))}
            </div>
            <Waterfall />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
